import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import {
  mapLayerSettingsSelector,
  StateWithPsaps,
} from '../capstone/psapInfo/psapInfoSlice';
import {
  canUserSeeFilteredSublayer,
  SERVICE_TO_FILTER,
  SUBLAYER_TO_FILTER,
} from './canUserSeeFilteredSublayer';
import { getLoadedGISServices } from './gisServicesAPI';
import {
  type GISService,
  type GISServicesState,
  type GISStateLayer,
  GISServiceTypes,
  MappedGISIdentity,
} from './gisServicesTypes';
import { reifyLayerMap } from './layer-loaders/buildLayerMap';

export const initialState: GISServicesState = {
  error: undefined,
  gisIdentities: [],
  services: [],
  layers: [],
  loading: false,
};

export const fetchGISServices = createAsyncThunk<
  {
    services: GISService[];
    gisIdentities: MappedGISIdentity[];
    layerMap: GISStateLayer[];
    roleName?: string;
    accountId?: string;
  },
  { orgName: string; shouldLoadLayers?: boolean },
  {
    state: StateWithPsaps & {
      gisServices: GISServicesState;
      sinatra?: {
        user?: {
          currentRole?: {
            name?: string;
            application?: string;
            permissions?: string[];
          };
        };
      };
    };
    rejectValue: { message: string; status?: number };
  }
>(
  'gisServices/fetchGISServices',
  async ({ orgName, shouldLoadLayers }, { rejectWithValue, getState }) => {
    try {
      const settings = mapLayerSettingsSelector(getState());
      const state = getState();
      const roleName = state.sinatra?.user?.currentRole?.name;
      const currentPSAP = state.psaps?.currentPSAP;
      const accountId = currentPSAP?.account_id;

      const result = await getLoadedGISServices(
        orgName,
        settings,
        shouldLoadLayers,
      );

      return {
        ...result,
        roleName,
        accountId,
      };
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue({
        message,
        status: (error as AxiosError).response?.status,
      });
    }
  },
);

const getLayer = (id: string, state: GISServicesState) => {
  return state.layers.find(l => l.id === id);
};

const getSublayer = (uniqueId: string, layer?: GISStateLayer) => {
  // We accept a missing layer so this can chain well with getLayer
  const sublayers = layer?.sublayers || [];
  return sublayers.find(l => l.uniqueId === uniqueId);
};

/**
 * Gets valid sublayers (excluding filtered ones that user can't see)
 */
function getValidSublayers(
  layer: GISStateLayer,
  accountId?: string,
  roleName?: string,
): typeof layer.sublayers {
  const canSeeFilteredSublayer = canUserSeeFilteredSublayer(
    accountId,
    roleName,
  );
  return layer.sublayers.filter(s => {
    // If this is the filtered sublayer and user can't see it, exclude it
    if (
      layer.title === SERVICE_TO_FILTER &&
      s.title === SUBLAYER_TO_FILTER &&
      !canSeeFilteredSublayer
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Calculates if the service checkbox should be checked based on valid sublayers
 * Service checkbox is checked if AT LEAST ONE valid sublayer is active
 */
function calculateServiceActiveState(
  layer: GISStateLayer,
  accountId?: string,
  roleName?: string,
): boolean {
  const validSublayers = getValidSublayers(layer, accountId, roleName);
  if (validSublayers.length === 0) {
    return false;
  }
  // Service is "active" (checkbox checked) if AT LEAST ONE valid sublayer is active
  return validSublayers.some(s => s.active);
}

/**
 * Filters layers based on user role and account ID
 * For users who cannot see the filtered sublayer, keep it in state but set active: false
 */
function filterLayersByUserContext(
  layerMap: GISStateLayer[],
  roleName?: string,
  accountId?: string,
): GISStateLayer[] {
  const canSee = canUserSeeFilteredSublayer(accountId, roleName);

  // If user can see it, no filtering needed
  if (canSee) {
    return layerMap;
  }

  // For users who can't see it, keep the sublayer but set active: false
  return layerMap.map(layer => {
    if (layer.title === SERVICE_TO_FILTER) {
      return {
        ...layer,
        sublayers: layer.sublayers.map(sublayer => {
          if (sublayer.title === SUBLAYER_TO_FILTER) {
            return {
              ...sublayer,
              active: false, // Always false for users who can't see it
            };
          }
          return sublayer;
        }),
      };
    }
    return layer;
  });
}

const gisServicesSlice = createSlice({
  name: 'gisServices',
  initialState,
  reducers: {
    clearAllGISServices: state => {
      state.layers.forEach(l => {
        l.active = false;
        l.sublayers.forEach(s => (s.active = false));
      });
    },
    updateSublayer: (
      state,
      action: PayloadAction<{
        sublayerID: string;
        layerID: string;
        active: boolean;
      }>,
    ) => {
      const { sublayerID, layerID, active } = action.payload;
      const layer = getLayer(layerID, state);
      const sublayer = getSublayer(sublayerID, layer);
      if (!layer || !sublayer) {
        // eslint-disable-next-line no-console
        console.error(
          'Attempted to update nonexistent layer:',
          layerID,
          sublayerID,
        );
        return;
      }

      // Check if this is the filtered sublayer and if user can see it
      const isFilteredSublayer =
        layer.title === SERVICE_TO_FILTER &&
        sublayer.title === SUBLAYER_TO_FILTER;

      if (isFilteredSublayer) {
        const canSee = canUserSeeFilteredSublayer(
          state.accountId,
          state.roleName,
        );
        // If user cannot see it, always keep active: false
        if (!canSee) {
          sublayer.active = false;
          return;
        }
      }

      // Note that the actual GIS toggling of visibility happens in emergency_data/src/hooks/useGISLayersVisibility.ts
      sublayer.active = active;

      // Recalculate service checkbox state based on valid sublayers
      // Service checkbox reflects if ALL valid sublayers are active
      layer.active = calculateServiceActiveState(
        layer,
        state.accountId,
        state.roleName,
      );
    },
    updateLayer: (
      state,
      action: PayloadAction<{ layerID: string; active: boolean }>,
    ) => {
      const { layerID, active } = action.payload;
      const layer = getLayer(layerID, state) as GISStateLayer;
      if (!layer) {
        // eslint-disable-next-line no-console
        console.error('Attempted to activate nonexistent layer:', layerID);
        return;
      }

      // Service layer is ALWAYS visible on the map
      // The checkbox state (layer.active) only reflects if all valid sublayers are active
      // When toggling the service checkbox, we only toggle the sublayers, not the service visibility

      // Check if user can see the filtered sublayer
      const canSeeFilteredSublayer = canUserSeeFilteredSublayer(
        state.accountId,
        state.roleName,
      );

      // Update all valid sublayers based on checkbox state
      // Filtered sublayers that user can't see remain inactive
      layer.sublayers.forEach(s => {
        if (
          layer.title === SERVICE_TO_FILTER &&
          s.title === SUBLAYER_TO_FILTER &&
          !canSeeFilteredSublayer
        ) {
          // Always keep filtered sublayer inactive if user can't see it
          s.active = false;
        } else {
          // All other sublayers (valid ones) toggle based on checkbox
          s.active = active;
        }
      });

      // Recalculate service checkbox state based on valid sublayers
      // This ensures the checkbox accurately reflects the state of valid sublayers
      layer.active = calculateServiceActiveState(
        layer,
        state.accountId,
        state.roleName,
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGISServices.pending, state => {
        state.loading = true;
      })
      .addCase(fetchGISServices.fulfilled, (state, action) => {
        state.error = initialState.error;
        state.loading = false;
        state.gisIdentities = action.payload.gisIdentities;
        state.services = action.payload.services;

        const roleName = action.payload.roleName;
        const accountId = action.payload.accountId;

        // Store accountId and roleName in state for use in reducers
        state.accountId = accountId;
        state.roleName = roleName;

        state.layers = filterLayersByUserContext(
          action.payload.layerMap,
          roleName,
          accountId,
        );
      })
      .addCase(fetchGISServices.rejected, (state, action) => {
        if (action.payload) {
          const { message = '', status = undefined } = action.payload;

          state = {
            ...initialState,
            error: {
              message: message,
              status: status,
            },
          };

          return state;
        }
      });
  },
});

export const { updateLayer, updateSublayer, clearAllGISServices } =
  gisServicesSlice.actions;

/**
 * SELECTORS:
 */

// Some components call these selectors in contexts where the slice is not
// loaded. This is not good practice, but it is simpler to handle that case
// here, for now.
const emptyServicesList: GISService[] = [];
export const selectGISServices = (state: { gisServices: GISServicesState }) => {
  return state.gisServices?.services || emptyServicesList;
};

export const selectBasemapService = createSelector(
  [selectGISServices],
  gisServices =>
    gisServices.find(l => l.service_type === GISServiceTypes.basemap),
);

export const selectSupplementalServices = createSelector(
  [selectGISServices],
  gisServices => {
    return gisServices.filter(
      l => l.service_type === GISServiceTypes.supplemental,
    );
  },
);

export const selectGeocodeService = createSelector(
  [selectGISServices],
  gisServices => {
    return gisServices.find(l => l.service_type === GISServiceTypes.geocode);
  },
);

const emptyLayersList: GISStateLayer[] = [];
/**
 * Returns a nested list of layers and sublayers for use in MapLayerControls, etc.
 */
export const selectGISLayersRaw = (state: {
  gisServices: GISServicesState;
}) => {
  return state.gisServices?.layers || emptyLayersList;
};

/**
 * Returns a nested list of layers and sublayers for use in MapLayerControls, etc.
 */
export const selectGISLayers = createSelector(
  selectGISLayersRaw,
  reifyLayerMap,
);

export const selectNonBasemapLayers = createSelector(
  selectGISLayers,
  layers => {
    return layers.filter(
      l => l.service.service_type != GISServiceTypes.basemap,
    );
  },
);

export const selectActiveLayers = createSelector(selectGISLayers, layers => {
  return layers.filter(l => l.active);
});

export const selectActiveServices = createSelector(
  selectActiveLayers,
  layers => {
    return layers.map(l => l.service);
  },
);

export const selectActiveClickToIdentifyServices = createSelector(
  selectActiveLayers,
  layers => {
    return layers.filter(
      l =>
        l.service.attributes?.is_click_to_identify_service ||
        l.service.service_type == GISServiceTypes.basemap,
    );
  },
);

/**
 * PERSISTENCE
 */

const persistConfig = {
  key: 'gisServices',
  storage,
  whitelist: [], // Do not persist on page refresh
};

export const gisServices = persistReducer(
  persistConfig,
  gisServicesSlice.reducer,
);
